import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { ReactNode, useState } from "react";

/** Width of the collapsed rail. Wide enough for a 34px icon button plus its focus ring. */
const SLIVER_WIDTH = 44;

/**
 * The column's height, handed down rather than left to the content.
 *
 * The panel inside ends in a paginator, and MRT drops that to the foot of its own paper with a
 * `margin-top: auto` — which moves it only while the paper has height left over to give. This
 * wrapper and `PanelBody` below it were plain blocks at their content height, so the paper was
 * exactly as tall as its rows and the paginator sat directly under the last template instead of at
 * the bottom of the column.
 *
 * A percentage against a parent with no definite height of its own resolves to `auto`, which is what
 * keeps this harmless on a compact pane: there the column is a row in a stack rather than a column
 * beside the tab, and the accordion is deliberately `height: auto`.
 */
const Panel = styled("div", { shouldForwardProp: (prop) => prop !== "minimized" })<{ minimized: boolean }>(
  ({ theme, minimized }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: "initial",
    ...(minimized && {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      gap: theme.spacing(0.5),
      overflow: "hidden",
      paddingTop: theme.spacing(1),
      width: SLIVER_WIDTH,
    }),
  }),
);
/**
 * Holds the panel itself, and takes the rest of the column — see `Panel`.
 *
 * The fill is written under `:not([hidden])` rather than on the element, because `hidden` is a UA
 * `display: none` and any author `display` here would outrank it: declared plainly, this would go on
 * drawing the whole panel behind the collapsed rail.
 */
const PanelBody = styled("div")({
  "&:not([hidden])": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
});
/**
 * The open column's first line: what the column is, and the control that puts it away (#6862). The
 * line held only the chevron before, so the column never said whose templates it listed; the title
 * costs no height, because the chevron's line was already paid for.
 */
const PanelHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  minWidth: 0,
  paddingBlock: theme.spacing(0.5),
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1),
}));
/** A heading over the column, set as the tab's other section headings are, not as a caption. */
const PanelTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
/**
 * The whole collapsed rail, as one control (#6862). Only the 34px chevron at its top answered a press
 * before; the label under it read as the thing to press and did nothing.
 */
const Rail = styled(ButtonBase)(({ theme }) => ({
  alignItems: "center",
  alignSelf: "stretch",
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.primary.main,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  justifyContent: "flex-start",
  paddingBlock: theme.spacing(0.5),
  "&:hover, &.Mui-focusVisible": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
}));
/**
 * How many are applied, on a line of its own under the chevron. As a `Badge` over the chevron it hid
 * most of the arrow at one digit and all of it at two (#6862).
 */
const RailCount = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: 10,
  color: theme.palette.primary.contrastText,
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: "20px",
  minWidth: 20,
  paddingInline: theme.spacing(0.5),
  textAlign: "center",
}));
const HiddenLabel = styled("span")({ ...visuallyHidden });
const SliverLabel = styled("span")(() => ({
  fontSize: "0.75rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  writingMode: "vertical-rl",
}));

type CollapsibleTemplatePanelProps = {
  /** When true the panel gives up its column and shows the rail instead. */
  minimized: boolean;
  /**
   * Whether the rail is the reader's own doing rather than a side pane's (#6621). Then "Show" puts the
   * column back for good, rather than peeking over the narrowed layout until the pane closes.
   */
  collapsedByReader?: boolean;
  /**
   * Offered, the open column carries a "Collapse templates" control, and the rail's "Show" reports
   * the column coming back. The caller keeps the answer; without it the column cannot be put away.
   */
  onCollapsedChange?: (isCollapsed: boolean) => void;
  label?: string;
  selectedCount?: number;
  children: ReactNode;
};

/**
 * The templates column of a proposal tab. Opening the comments/selection side pane used to hide this
 * outright, which left no way back to the templates without closing the pane; now it collapses to a
 * labelled rail that can be expanded back over the narrowed layout for as long as the pane stays open.
 */
export default function CollapsibleTemplatePanel(props: Readonly<CollapsibleTemplatePanelProps>) {
  const { minimized, children, collapsedByReader, onCollapsedChange } = props;
  const label = props.label ?? "Templates";
  const selectedCount = props.selectedCount ?? 0;

  const [wasMinimized, setWasMinimized] = useState(minimized);
  const [isPeeking, setIsPeeking] = useState(false);
  // A peek belongs to the side-pane session that prompted it: reopening the pane starts back at the rail.
  if (wasMinimized !== minimized) {
    setWasMinimized(minimized);
    setIsPeeking(false);
  }

  const showSliver = minimized && !isPeeking;
  const lowerLabel = label.toLowerCase();

  /**
   * The rail's one control. A rail the reader asked for opens the column again for good; a side pane's
   * rail lets them peek. Both at once — put away, then a side pane opened — opens it for good and peeks
   * over the pane, so one press is enough either way.
   */
  function handleShowPressed() {
    if (collapsedByReader) onCollapsedChange?.(false);
    setIsPeeking(true);
  }

  return (
    <Panel minimized={showSliver}>
      {showSliver && (
        <Tooltip title={`Show ${lowerLabel}`} placement="right">
          <Rail aria-label={`Show ${lowerLabel}`} onClick={handleShowPressed}>
            <ChevronRightIcon />
            {selectedCount > 0 && (
              <RailCount>
                {selectedCount}
                <HiddenLabel> selected</HiddenLabel>
              </RailCount>
            )}
            <SliverLabel>{label}</SliverLabel>
          </Rail>
        </Tooltip>
      )}
      {!minimized && onCollapsedChange && (
        <PanelHeader>
          <PanelTitle variant="subtitle1">{label}</PanelTitle>
          <Tooltip title={`Collapse ${lowerLabel}`} placement="left">
            <IconButton
              aria-label={`Collapse ${lowerLabel}`}
              color="primary"
              size="small"
              onClick={() => onCollapsedChange(true)}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        </PanelHeader>
      )}
      {minimized && isPeeking && (
        <PanelHeader>
          <PanelTitle variant="subtitle1">{label}</PanelTitle>
          <Tooltip title={`Hide ${lowerLabel}`} placement="left">
            <IconButton aria-label={`Hide ${lowerLabel}`} color="primary" size="small" onClick={() => setIsPeeking(false)}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        </PanelHeader>
      )}
      <PanelBody hidden={showSliver}>{children}</PanelBody>
    </Panel>
  );
}
